import React from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useDroppable } from "@dnd-kit/core";
import { Button, Collapse, Empty, Typography } from "antd";
import dayjs from "dayjs";

import { type TeamMemberTaskWithTrainee } from "@fitness-app/data-models/entities/TeamMemberTask";

import { useTeamMemberTaskContext } from "../context/TeamMemberTaskContext";
import { DraggableTaskItem } from "./DraggableTaskItem";

interface DroppableDateSectionProps {
  id: string;
  title: string;
  date?: string | null;
  tasks: TeamMemberTaskWithTrainee[];
  isOverdue?: boolean;
  showAddButton?: boolean;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  disableDrag?: boolean;
}

export const DroppableDateSection: React.FC<DroppableDateSectionProps> = ({
  id,
  title,
  date,
  tasks,
  isOverdue = false,
  showAddButton = true,
  defaultOpen = true,
  isOpen,
  onToggle,
  disableDrag = false,
}) => {
  const { openTaskForm } = useTeamMemberTaskContext();
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: { date },
  });

  const handleAddTask = () => {
    openTaskForm({
      dueDate: {
        date: date || dayjs().format("YYYY-MM-DD"),
        time: null,
        timezone: "Europe/Warsaw",
      },
    });
  };

  const formatDateHeader = () => {
    if (!date) return title;

    const taskDate = dayjs(date);
    const today = dayjs().startOf("day");

    if (taskDate.isSame(today, "day")) {
      return "Dzisiaj";
    }

    if (taskDate.isSame(today.add(1, "day"), "day")) {
      return "Jutro";
    }

    return taskDate.format("D MMMM");
  };

  const taskCount = tasks.length;

  const collapseProps =
    isOpen !== undefined
      ? {
          activeKey: isOpen ? ["1"] : [],
          onChange: (keys: string | string[]) => onToggle?.(Array.isArray(keys) ? keys.length > 0 : Boolean(keys)),
        }
      : { defaultActiveKey: defaultOpen ? ["1"] : [] };

  return (
    <div ref={setNodeRef} className={`rounded-lg transition-colors ${isOver ? "bg-blue-50 ring-2 ring-blue-300" : ""}`}>
      <Collapse
        {...collapseProps}
        ghost
        className="task-date-section"
        items={[
          {
            key: "1",
            label: (
              <div className="flex items-center gap-2">
                <Typography.Text
                  strong
                  type={isOverdue ? "danger" : undefined}
                  className={isOverdue ? "text-red-600" : ""}
                >
                  {formatDateHeader()}
                </Typography.Text>
                <Typography.Text type="secondary" className="text-sm">
                  ({taskCount})
                </Typography.Text>
              </div>
            ),
            children: (
              <div className="min-h-[40px] space-y-2">
                {tasks.length === 0 && !isOver && (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak zadań" className="py-2" />
                )}

                {tasks.length === 0 && isOver && (
                  <div className="flex items-center justify-center rounded-md border-2 border-dashed border-blue-300 bg-blue-50 py-4 text-blue-500">
                    Upuść tutaj
                  </div>
                )}

                {tasks.map((task) => (
                  <DraggableTaskItem key={task.id} task={task} disabled={disableDrag} />
                ))}

                {showAddButton && (
                  <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddTask} className="w-full">
                    Dodaj zadanie
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
